import ErrorIcon from "@mui/icons-material/Error";
import { Box, ListItemButton, ListItemText, Skeleton, Tooltip, tooltipClasses, Typography } from "@mui/material";
import { styled } from "@mui/styles";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectStationFilter } from "../../slices/userSession/userSessionSlice";

const CustomWidthTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "15rem",
  },
});

const useFetch = (fetchFn, args, workgroupFilters, websockets, websocketRefreshTimeMS) => {
  const stationFilter = useSelector(selectStationFilter);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const isFirstLoadRef = useRef(true);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setFetchError("");
      const result = await fetchFn(args, workgroupFilters);
      setData(result);
    } catch (e) {
      console.error("Error fetching snapshot data:", e);
      setFetchError("Unexpected Error");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFn, ...args]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, stationFilter, workgroupFilters]);

  useEffect(() => {
    if (!isFirstLoadRef.current) {
      setTimeout(() => {
        fetchData();
      }, websocketRefreshTimeMS);
    }
    isFirstLoadRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websockets]);

  return { data, isLoading, fetchError };
};

const DashboardAdminSnapshotCount = (props) => {
  const websocketData = useSelector((state) => state.webSocketEvent[props.websockets]);

  const { data, isLoading, fetchError } = useFetch(
    props.fetchFunc,
    props.fetchFuncParams,
    props.workgroupFilters,
    websocketData,
    props.websocketRefreshTimeMS
  );

  return (
    <CustomWidthTooltip
      title={fetchError !== "" ? fetchError : props.tooltip}
      placement="right"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -40],
              },
            },
          ],
        },
      }}
    >
      <ListItemButton
        sx={{ marginLeft: "15px", marginRight: "15px" }}
        href={`/#${props.feature.path}`}
        onClick={props.onClick}
      >
        <Box width={4} height={40} mr={1} sx={{ backgroundColor: props.color }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 400, fontSize: "31px", height: "40px", display: "inline-flex", alignItems: "center" }}
        >
          {isLoading ? (
            <Skeleton variant="rounded" width={18} height={35} />
          ) : fetchError !== "" ? (
            <ErrorIcon sx={{ color: "primary.swaRed" }} />
          ) : (
            data.length
          )}
        </Typography>
        <ListItemText primary={props.text} primaryTypographyProps={{ fontSize: "14px" }} sx={{ marginLeft: 3 }} />
      </ListItemButton>
    </CustomWidthTooltip>
  );
};

DashboardAdminSnapshotCount.propTypes = {
  fetchFunc: PropTypes.func.isRequired,
  fetchFuncParams: PropTypes.array.isRequired,
  websockets: PropTypes.string.isRequired,
  websocketRefreshTimeMS: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  feature: PropTypes.object.isRequired,
  tooltip: PropTypes.string.isRequired,
  workgroupFilters: PropTypes.array.isRequired,
};

export default DashboardAdminSnapshotCount;
